import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { CampaignUserModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/campaignUser/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/campaignUser/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/campaignUser/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/campaignUser/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/campaignUser/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/campaignUser/CREATE_FAILURE'

export type CampaignUserModuleState = {
  creating: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
}

// Initial State
const initialState: CampaignUserModuleState = {
  creating: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadCampaignUsers(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().campaignUsers.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/campaign-user-assignments', options)
      .then(({ data }: { data: { data: CampaignUserModel[] } }) => {
        const normalizedJson = normalize(data.data, Schemas.CAMPAIGN_USER_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data: data.data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

type BulkCreateCampaignUsersParams = {
  campaignId: number,
  leadUserIds: number[],
  supportUserIds: number[],
}

export function bulkCreateCampaignUsers(params: BulkCreateCampaignUsersParams, options: EntityOptions = {}) {
  const { campaignId } = params

  const config = {
    method: 'POST',
    data: JSON.stringify({
      ...params,
    }),
  }

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    dispatch(createRequest())

    const promise = api('/campaign-user-assignments/bulk', options, config)
      .then(({ data }: { data: CampaignUserModel[] }) => {
        const { campaignUsers } = getState().entities
        Object.values(campaignUsers).forEach((campaignUser) => {
          if (campaignUser.campaignId === campaignId){
            const normalizedCampaignUser = normalize(campaignUser, Schemas.CAMPAIGN_USER)
            dispatch(deleteEntity(normalizedCampaignUser))
          }
        })

        const normalizedJson = normalize(data, Schemas.CAMPAIGN_USER_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: CampaignUserModuleState = initialState,
  action: Action = { type: '' },
): CampaignUserModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
